import './style.scss';
import SelectType from './select-type';
import MultiSelectType from './multi-select-type';
import ButtonType from './button-type';
import SearchType from './search-type';
import ClearButton from './clear-button';
import ActiveTags from './active-tags';
import ApplyButton from './apply-button';
import clearUrl from './helpers/clear-url';
import isEmpty from './helpers/is-empty';
import updateHistory from './helpers/update-history';

class Filters {
    constructor(module) {
        this.block = module;
        this.activeFiltersTag = module.querySelector('.gravity-block-filters__active-tag');
        this.applyFiltersButton = module.querySelector('.gravity-block-filters__apply-filter');
        this.clearFiltersButton = module.querySelector('.gravity-block-filters__reset-all');
        this.filterArgs = {
            content_type: [],
            taxonomy: {},
            order_by: '',
            order: '',
        };
        this.filters = [...module.querySelectorAll('.gravity-block-filters__type')].map((item) => {
            const filterType = item.dataset?.type;

            switch (filterType) {
                case 'select':
                    return new SelectType(item, this);
                case 'multi-select':
                    return new MultiSelectType(item, this);
                case 'search':
                    return new SearchType(item, this);
                case 'button':
                    return new ButtonType(item, this);
                default:
                    throw new Error('Type not supported');
            }
        });
    }

    get hasApplyButton() {
        return this.applyFiltersButton !== null;
    }

    get hasActiveTag() {
        return this.activeFiltersTag !== null;
    }

    get hasClearButton() {
        return this.clearFiltersButton !== null;
    }

    get isActive() {
        return (
            !this.filterArgs.content_type?.length &&
            (!this.filterArgs.taxonomy || !Object.keys(this.filterArgs.taxonomy).length) &&
            !this.filterArgs.search &&
            !this.filterArgs?.date_range_type &&
            (!this.filterArgs?.order_by || !this.filterArgs?.order)
        );
    }

    init() {
        if (this.filters.length > 0) {
            if (this.hasActiveTag) {
                new ActiveTags(this.activeFiltersTag, this).init();
            }

            if (this.hasClearButton) {
                new ClearButton(this.clearFiltersButton, this).init();
            }

            if (this.hasApplyButton) {
                new ApplyButton(this.applyFiltersButton, this).init();
            }

            this.maybeUpdateFromUrlParams();
            this.initFilters();
        }
    }

    initFilters() {
        this.filters.forEach((item) => item.init());
    }

    sendFiltersChange(element) {
        this.updateUrlParams();

        element.dispatchEvent(
            new CustomEvent('gg-filter-changed', {
                bubbles: true,
                detail: { filter: this.filterArgs },
            })
        );
    }

    updateUrlParams() {
        const filterParams = { ...this.filterArgs };

        // Stringify taxonomy object
        Object.keys(filterParams).forEach((propertyKey) => {
            if (isEmpty(filterParams[propertyKey])) {
                delete filterParams[propertyKey];
                return;
            }

            if (propertyKey === 'taxonomy' && typeof filterParams[propertyKey] === 'object') {
                filterParams[propertyKey] = this.strignifyTaxonomy(filterParams[propertyKey]);
            }
        });

        const searchParams = new URLSearchParams(filterParams).toString();

        if (searchParams) {
            // Update the URL without refresh with new params
            updateHistory(`?${searchParams}`, false);
        } else {
            clearUrl();
        }
    }

    // eslint-disable-next-line class-methods-use-this
    strignifyTaxonomy(object) {
        return Object.entries(object)
            .map((taxonomy) => `${taxonomy[0]}:${taxonomy[1].join(',')}`)
            .join(';');
    }

    // Parsing Taxonomy URLs in format like: category:term1,term2;category2:term4
    // eslint-disable-next-line class-methods-use-this
    parseTaxonomyUrl(url) {
        return Object.fromEntries(
            url.split(';').map((taxonomy) => {
                const [categorySlug, terms] = taxonomy.split(':');

                return [categorySlug, terms.split(',')];
            })
        );
    }

    maybeUpdateFromUrlParams() {
        const paramsFromURL = new URLSearchParams(window.location.search);
        const params = {};

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of paramsFromURL.entries()) {
            switch (key) {
                case 'taxonomy':
                    params[key] = this.parseTaxonomyUrl(value);
                    break;
                case 'content_type':
                    params[key] = value.split(',');
                    break;
                default:
                    params[key] = value;
                    break;
            }
        }

        if (Object.keys(params).length) {
            this.filterArgs = params;
            this.sendFiltersChange(this.block);
        }
    }
}

document.querySelectorAll('.gravity-block-filters').forEach((module) => {
    new Filters(module).init();
});
